import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["list", "emptyState", "searchInput", "searchResults"]
  static values = { searchUrl: String, saveUrl: String }

  connect() {
    this.sortable = Sortable.create(this.listTarget, {
      handle: "[data-sortable-handle]",
      animation: 150
    })
    this.debounceTimer = null
  }

  disconnect() {
    this.sortable?.destroy()
  }

  onSearch() {
    clearTimeout(this.debounceTimer)
    const query = this.searchInputTarget.value.trim()
    if (query.length < 2) { this.hideResults(); return }
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 300)
  }

  async fetchResults(query) {
    const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
    const response = await fetch(url, { headers: { "Accept": "application/json" } })
    const articles = await response.json()
    const existingIds = this.currentArticleIds()

    const filtered = articles.filter(a => !existingIds.includes(a.id))
    if (filtered.length === 0) { this.hideResults(); return }

    this.searchResultsTarget.innerHTML = filtered.map(a =>
      `<button type="button" class="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm"
              data-action="featured-articles#addFromResult"
              data-article-id="${a.id}" data-article-title="${this.escapeHtml(a.title)}"
              data-article-date="${a.published_at || ''}">${this.escapeHtml(a.title)}
        <span class="text-xs text-gray-400 ml-1">${a.published_at || ''}</span>
      </button>`
    ).join("")
    this.searchResultsTarget.classList.remove("hidden")
  }

  addFromResult(event) {
    const btn = event.currentTarget
    const id = btn.dataset.articleId
    const title = btn.dataset.articleTitle
    const date = btn.dataset.articleDate

    const html = `<div class="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200"
         data-article-id="${id}">
      <span class="cursor-grab text-gray-400" data-sortable-handle><i class="fas fa-grip-vertical"></i></span>
      <div class="flex-1 min-w-0">
        <span class="text-sm font-medium text-gray-900 truncate block">${this.escapeHtml(title)}</span>
        <span class="text-xs text-gray-500">${date}</span>
      </div>
      <button type="button" class="text-gray-400 hover:text-red-500" data-action="featured-articles#removeArticle">
        <i class="fas fa-times"></i>
      </button>
    </div>`
    this.listTarget.insertAdjacentHTML("beforeend", html)
    this.hideResults()
    this.searchInputTarget.value = ""
    this.toggleEmptyState()
  }

  removeArticle(event) {
    event.currentTarget.closest("[data-article-id]").remove()
    this.toggleEmptyState()
  }

  async save() {
    const ids = this.currentArticleIds()
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    await fetch(this.saveUrlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ featured_article_ids: ids })
    })
    window.location.reload()
  }

  // --- Helpers ---

  currentArticleIds() {
    return Array.from(this.listTarget.querySelectorAll("[data-article-id]"))
      .map(el => el.dataset.articleId)
  }

  toggleEmptyState() {
    const hasItems = this.listTarget.querySelectorAll("[data-article-id]").length > 0
    this.emptyStateTarget.classList.toggle("hidden", hasItems)
  }

  hideResults() {
    this.searchResultsTarget.classList.add("hidden")
    this.searchResultsTarget.innerHTML = ""
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
